import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PreferencesStep = ({ formData, updateFormData, errors, onNext, onPrevious }) => {
  const locations = [
    { value: 'oslo', label: 'Oslo' },
    { value: 'bergen', label: 'Bergen' },
    { value: 'trondheim', label: 'Trondheim' },
    { value: 'stavanger', label: 'Stavanger' },
    { value: 'kristiansand', label: 'Kristiansand' },
    { value: 'tromsø', label: 'Tromsø' },
    { value: 'drammen', label: 'Drammen' },
    { value: 'fredrikstad', label: 'Fredrikstad' },
    { value: 'remote', label: 'Remote Only' },
    { value: 'anywhere', label: 'Anywhere in Norway' }
  ];

  const availabilityOptions = [
    { value: 'immediately', label: 'Immediately' },
    { value: '2-weeks', label: 'Within 2 weeks' },
    { value: '1-month', label: 'Within 1 month' },
    { value: '2-months', label: 'Within 2 months' },
    { value: '3-months', label: 'Within 3 months' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const workPermitOptions = [
    { value: 'citizen', label: 'Norwegian/EU Citizen' },
    { value: 'permit', label: 'Valid Work Permit' },
    { value: 'applying', label: 'Applying for Permit' },
    { value: 'sponsorship', label: 'Need Sponsorship' }
  ];

  const employmentTypes = [
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' }
  ];

  const workArrangements = [
    { value: 'office', label: 'Office-based' },
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' }
  ];

  const handleEmploymentTypeChange = (type, checked) => {
    const currentTypes = formData?.employmentTypes || [];
    if (checked) {
      updateFormData('employmentTypes', [...currentTypes, type]);
    } else {
      updateFormData('employmentTypes', currentTypes?.filter(t => t !== type));
    }
  };

  const handleWorkArrangementChange = (arrangement, checked) => {
    const currentArrangements = formData?.workArrangements || [];
    if (checked) {
      updateFormData('workArrangements', [...currentArrangements, arrangement]);
    } else {
      updateFormData('workArrangements', currentArrangements?.filter(a => a !== arrangement));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Work Preferences
        </h2>
        <p className="text-muted-foreground">
          Tell us about your ideal work situation and availability
        </p>
      </div>
      <div className="space-y-6">
        {/* Location Preferences */}
        <Select
          label="Preferred Location"
          description="Where would you like to work?"
          options={locations}
          value={formData?.location}
          onChange={(value) => updateFormData('location', value)}
          error={errors?.location}
          searchable
          required
        />

        {/* Availability */}
        <Select
          label="Availability"
          description="When can you start a new position?"
          options={availabilityOptions}
          value={formData?.availability}
          onChange={(value) => updateFormData('availability', value)}
          error={errors?.availability}
          required
        />

        {/* Work Permit Status */}
        <Select
          label="Work Authorization"
          description="Your current work authorization status in Norway"
          options={workPermitOptions}
          value={formData?.workPermit}
          onChange={(value) => updateFormData('workPermit', value)}
          error={errors?.workPermit}
          required
        />

        {/* Employment Types */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Employment Type *
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Select all employment types you're interested in
          </p>
          <div className="space-y-2">
            {employmentTypes?.map((type) => (
              <Checkbox
                key={type?.value}
                label={type?.label}
                checked={(formData?.employmentTypes || [])?.includes(type?.value)}
                onChange={(e) => handleEmploymentTypeChange(type?.value, e?.target?.checked)}
              />
            ))}
          </div>
          {errors?.employmentTypes && (
            <p className="text-error text-sm mt-1">{errors?.employmentTypes}</p>
          )}
        </div>

        {/* Work Arrangements */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Work Arrangement *
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Select all work arrangements you're open to
          </p>
          <div className="space-y-2">
            {workArrangements?.map((arrangement) => (
              <Checkbox
                key={arrangement?.value}
                label={arrangement?.label}
                checked={(formData?.workArrangements || [])?.includes(arrangement?.value)}
                onChange={(e) => handleWorkArrangementChange(arrangement?.value, e?.target?.checked)}
              />
            ))}
          </div>
          {errors?.workArrangements && (
            <p className="text-error text-sm mt-1">{errors?.workArrangements}</p>
          )}
        </div>

        {/* Salary Expectations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Minimum Salary (NOK)"
            type="number"
            placeholder="500000"
            description="Annual salary expectation"
            value={formData?.minSalary}
            onChange={(e) => updateFormData('minSalary', e?.target?.value)}
          />

          <Input
            label="Maximum Salary (NOK)"
            type="number"
            placeholder="800000"
            description="Optional upper range"
            value={formData?.maxSalary}
            onChange={(e) => updateFormData('maxSalary', e?.target?.value)}
          />
        </div>

        {/* Portfolio/Website */}
        <Input
          label="Portfolio/Website"
          type="url"
          placeholder="https://yourportfolio.com"
          description="Link to your portfolio, GitHub, or personal website"
          value={formData?.portfolio}
          onChange={(e) => updateFormData('portfolio', e?.target?.value)}
        />

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Information
          </label>
          <textarea
            placeholder="Any additional information you'd like employers to know (optional)..."
            value={formData?.additionalInfo}
            onChange={(e) => updateFormData('additionalInfo', e?.target?.value)}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {formData?.additionalInfo?.length}/500 characters
          </p>
        </div>

        {/* Privacy Preferences */}
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h4 className="font-medium text-foreground mb-3 flex items-center">
            <Icon name="Shield" size={20} className="mr-2" />
            Privacy Settings
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Allow employers to see my profile in search results"
              description="Uncheck to make your profile completely private"
              checked={formData?.allowSearch !== false}
              onChange={(e) => updateFormData('allowSearch', e?.target?.checked)}
            />
            
            <Checkbox
              label="Send me email notifications when employers view my profile"
              checked={formData?.emailNotifications !== false}
              onChange={(e) => updateFormData('emailNotifications', e?.target?.checked)}
            />
            
            <Checkbox
              label="Allow employers to save my profile to their shortlists"
              checked={formData?.allowShortlist !== false}
              onChange={(e) => updateFormData('allowShortlist', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={onNext}
          iconName="ArrowRight"
          iconPosition="right"
          disabled={
            !formData?.location || 
            !formData?.availability || 
            !formData?.workPermit ||
            !formData?.employmentTypes?.length ||
            !formData?.workArrangements?.length
          }
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default PreferencesStep;